import { useMemo, useState } from "react";
import { FUND } from "@/lib/content";
import { createCheckout } from "@/lib/api";
import { Loader2, CreditCard } from "lucide-react";
import { toast } from "sonner";

export default function DonationWidget() {
  const [packageId, setPackageId] = useState("champion");
  const [custom, setCustom] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [anonymous, setAnonymous] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const selectedAmount = useMemo(() => {
    if (custom) {
      const n = parseFloat(custom);
      return isNaN(n) ? 0 : n;
    }
    return FUND.packages.find((p) => p.id === packageId)?.amount || 0;
  }, [packageId, custom]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;

    if (!name.trim()) {
      toast.error("Please enter your name (or 'Anonymous')");
      return;
    }
    if (!selectedAmount || selectedAmount <= 0) {
      toast.error("Pick an amount or enter a custom amount");
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        donor_name: name.trim(),
        donor_email: email.trim() || null,
        message: message.trim() || null,
        is_anonymous: anonymous,
        origin_url: window.location.origin,
      };
      if (custom) {
        payload.custom_amount = parseFloat(custom);
      } else {
        payload.package_id = packageId;
      }

      const res = await createCheckout(payload);
      if (res?.url) {
        window.location.href = res.url;
      } else {
        throw new Error("Missing checkout URL");
      }
    } catch (err) {
      console.error(err);
      const detail = err?.response?.data?.detail || err?.message || "Payment error";
      toast.error(detail);
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="donation-widget"
      className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32"
    >
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
        {/* Left: Heading & context */}
        <div className="lg:col-span-5">
          <div className="text-[10px] uppercase tracking-[0.35em] text-[#FF007F]">
            Step up • Send love
          </div>
          <h2 className="font-display uppercase tracking-tight text-5xl md:text-6xl lg:text-7xl mt-4 leading-[0.9]">
            Every dollar<br />
            <span className="text-[#00E5FF]">becomes a rep.</span>
          </h2>
          <p className="text-[#A1A1AA] text-lg mt-6 font-light leading-relaxed">
            Pick a tier or set your own. 100% of your gift goes toward Camden's NATTYS
            2026 trip — tournament entry, travel to Mesa, hotel, and per-diem. Secure
            checkout via Stripe.
          </p>

          <div className="mt-10 grid grid-cols-2 gap-4 text-sm">
            <div className="border-l-2 border-[#00E5FF] pl-4">
              <div className="text-[10px] uppercase tracking-[0.25em] text-[#71717A]">Secure</div>
              <div className="text-white font-semibold">Stripe checkout</div>
            </div>
            <div className="border-l-2 border-[#FF007F] pl-4">
              <div className="text-[10px] uppercase tracking-[0.25em] text-[#71717A]">Tax</div>
              <div className="text-white font-semibold">Personal gift</div>
            </div>
          </div>
        </div>

        {/* Right: Form */}
        <form
          onSubmit={onSubmit}
          className="lg:col-span-7 bg-[#0B0B0D] border border-[#27272A] p-6 md:p-10 backdrop-blur-xl"
        >
          {/* Tier chips */}
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#A1A1AA] mb-3">
            Pick a tier
          </div>
          <div className="grid grid-cols-3 sm:grid-cols-3 lg:grid-cols-3 gap-3">
            {FUND.packages.map((p) => {
              const active = packageId === p.id && !custom;
              return (
                <button
                  type="button"
                  key={p.id}
                  data-testid={`donate-amount-chip-${p.amount}`}
                  onClick={() => {
                    setPackageId(p.id);
                    setCustom("");
                  }}
                  className={`text-left px-4 py-3 border transition-all ${
                    active
                      ? "border-[#00E5FF] bg-[#00E5FF]/10"
                      : "border-[#27272A] bg-[#18181B] hover:border-[#52525B]"
                  }`}
                >
                  <div className={`font-display text-2xl ${active ? "text-[#00E5FF]" : "text-white"}`}>
                    {p.label}
                  </div>
                  <div className="text-[10px] uppercase tracking-[0.18em] text-[#A1A1AA] mt-1">
                    {p.subtitle}
                  </div>
                </button>
              );
            })}
          </div>

          {/* Custom amount */}
          <div className="mt-6">
            <label className="block text-[10px] uppercase tracking-[0.3em] text-[#A1A1AA] mb-2">
              Or enter a custom amount (USD)
            </label>
            <div className="flex items-center bg-[#09090B] border border-[#27272A] focus-within:border-[#FF007F] transition-colors">
              <span className="px-4 text-[#71717A] font-display text-xl">$</span>
              <input
                data-testid="donate-custom-input"
                type="number"
                inputMode="decimal"
                step="0.01"
                min="1"
                max="5000"
                placeholder="Custom amount"
                value={custom}
                onChange={(e) => setCustom(e.target.value)}
                className="w-full bg-transparent text-white py-3 pr-4 outline-none font-display text-xl tabular"
              />
            </div>
          </div>

          {/* Name, email, message */}
          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-[10px] uppercase tracking-[0.3em] text-[#A1A1AA] mb-2">
                Your name
              </label>
              <input
                data-testid="donate-name-input"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Jane Doe"
                maxLength={80}
                required
                className="w-full bg-[#09090B] border border-[#27272A] focus:border-[#FF007F] outline-none text-white px-4 py-3"
              />
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-[0.3em] text-[#A1A1AA] mb-2">
                Email (optional)
              </label>
              <input
                data-testid="donate-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="w-full bg-[#09090B] border border-[#27272A] focus:border-[#FF007F] outline-none text-white px-4 py-3"
              />
            </div>
          </div>

          <div className="mt-6">
            <label className="block text-[10px] uppercase tracking-[0.3em] text-[#A1A1AA] mb-2">
              Message of encouragement (optional)
            </label>
            <textarea
              data-testid="donate-message-input"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              maxLength={280}
              rows={3}
              placeholder="Keep working, Camden. We're with you. 🏀"
              className="w-full bg-[#09090B] border border-[#27272A] focus:border-[#FF007F] outline-none text-white px-4 py-3 resize-none"
            />
            <div className="text-[10px] text-[#52525B] mt-1 text-right">{message.length}/280</div>
          </div>

          <label className="mt-2 inline-flex items-center gap-2 text-sm text-[#A1A1AA] cursor-pointer">
            <input
              data-testid="donate-anonymous-checkbox"
              type="checkbox"
              checked={anonymous}
              onChange={(e) => setAnonymous(e.target.checked)}
              className="accent-[#FF007F]"
            />
            Display as Anonymous on the donor wall
          </label>

          {/* Submit row */}
          <div className="mt-8">
            <button
              type="submit"
              disabled={submitting}
              data-testid="donate-submit-button"
              className="w-full inline-flex items-center justify-center gap-3 bg-[#FF007F] text-white font-bold uppercase tracking-[0.2em] py-4 px-6 hover:-translate-y-0.5 transition-transform shadow-pink-glow disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {submitting ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                <CreditCard className="h-5 w-5" />
              )}
              {submitting ? "Redirecting…" : `Give $${selectedAmount.toFixed(2)} securely`}
            </button>
          </div>

          <p className="text-[11px] text-[#52525B] mt-4">
            Payments are processed securely by Stripe. You'll receive an email receipt. This
            is a personal gift, not a tax-deductible donation.
          </p>
        </form>
      </div>
    </div>
  );
}
